import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonSearch from '@mui/icons-material/PersonSearch';
import NotesIcon from '@mui/icons-material/Notes';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { Edit } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ClienteSearch from './ClienteSearch';
import api from '../../services/api';
import companiaService from '../../services/companiaService';
import cuentaCorrienteService from '../../services/cuentaCorrienteService';
import devolucionVentaService from '../../services/devolucionVentaService';
import tesoreriaService, { GrupoCajaChica, MedioTransferencia } from '../../services/tesoreriaService';
import clienteService, {
  esErrorClienteNoOperativo,
  getMensajeClienteNoOperativo,
} from '../../services/clienteService';
import { formatComprobanteTesoreria, formatCurrency, truncToDecimals } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';
import { effectiveUsername } from '../../utils/sistemaAuthz';
import { useSucursalAltaFormulario } from '../../hooks/useSucursalAltaFormulario';
import {
  filtrarSucursalesPorCompania,
  resolveNroSucParaCompania,
} from '../../utils/sucursalPreferida';
import { persistGlobalIdCompania } from '../../utils/companiaPreferida';
import { leerGrupoCajaGuardadoRaw, persistGrupoCajaChica } from '../../utils/grupoCajaStorage';

const TIPO_IVA_LABELS: Record<string, string> = {
  I: 'Inscripto',
  N: 'No Inscripto',
  E: 'Exento',
  M: 'Monotributo',
  RI: 'Resp. Inscripto',
  CF: 'Consumidor Final',
  EX: 'Exento',
  MO: 'Monotributista',
  NI: 'No Inscripto',
};

interface Sucursal {
  nroSuc: number;
  nombreSucursal: string;
  idCompania?: string | null;
}

interface FormularioData {
  nroCliente: number;
  nombre: string;
  cuit: string;
  tipoIVA: string;
  nroSuc: number;
  fechaEmision: string;
  descripcion: string;
  idCompania: string;
}

interface ReintegroData {
  tipoPago: string;
  mcp?: string;
  medioLabel?: string;
  importe: number;
  detalle?: string;
  grupoMcp?: number;
}

const initialReintegro: ReintegroData = {
  tipoPago: 'Efectivo',
  importe: 0,
};

const NuevaDevolucion: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const loggedUsername = effectiveUsername(user?.username) || 'Sistema';

  const [formulario, setFormulario] = useState<FormularioData>({
    nroCliente: 0,
    nombre: '',
    cuit: '',
    tipoIVA: '',
    nroSuc: 0,
    fechaEmision: new Date().toISOString().split('T')[0],
    descripcion: '',
    idCompania: '',
  });

  const [companias, setCompanias] = useState<{ idCompania: string; nombre: string }[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [loadingSucursales, setLoadingSucursales] = useState(true);
  const [loadingInit, setLoadingInit] = useState(true);
  const [saving, setSaving] = useState(false);

  const [saldoTotal, setSaldoTotal] = useState(0);
  const [saldoVencido, setSaldoVencido] = useState(0);
  const [saldoVigente, setSaldoVigente] = useState(0);
  const [loadingSaldo, setLoadingSaldo] = useState(false);

  const [reintegro, setReintegro] = useState<ReintegroData | null>(null);
  const [openClienteSearch, setOpenClienteSearch] = useState(false);

  const [openReintegroDialog, setOpenReintegroDialog] = useState(false);
  const [reintegroDraft, setReintegroDraft] = useState<ReintegroData>(initialReintegro);
  const [activeStep, setActiveStep] = useState(0);
  const [isMontoFocused, setIsMontoFocused] = useState(false);
  const [montoDraft, setMontoDraft] = useState('');

  const [openGrupoCajaDialog, setOpenGrupoCajaDialog] = useState(false);
  const [gruposCaja, setGruposCaja] = useState<GrupoCajaChica[]>([]);
  const [grupoCajaLoading, setGrupoCajaLoading] = useState(false);
  const [grupoCajaSeleccionadoId, setGrupoCajaSeleccionadoId] = useState<number | null>(null);
  const [grupoCajaSeleccionado, setGrupoCajaSeleccionado] = useState<GrupoCajaChica | null>(null);
  const [grupoCajaPage, setGrupoCajaPage] = useState(1);
  const [grupoCajaHasMore, setGrupoCajaHasMore] = useState(true);
  const [mediosEfectivo, setMediosEfectivo] = useState<MedioTransferencia[]>([]);
  const [efectivoSeleccionadoId, setEfectivoSeleccionadoId] = useState<number | null>(null);

  const totalReintegro = useMemo(() => reintegro?.importe ?? 0, [reintegro]);
  const clienteBloqueado = reintegro != null && totalReintegro > 0;

  const cargarSaldoCliente = useCallback(async (nro: number) => {
    if (!nro || nro <= 0) {
      setSaldoTotal(0);
      setSaldoVencido(0);
      setSaldoVigente(0);
      return;
    }
    setLoadingSaldo(true);
    try {
      const saldo = await cuentaCorrienteService.getSaldoCliente(nro);
      setSaldoTotal(Number(saldo.saldoTotal ?? 0));
      setSaldoVencido(Number(saldo.saldoVencido ?? 0));
      setSaldoVigente(Number(saldo.saldoVigente ?? 0));
    } catch {
      setSaldoTotal(0);
      setSaldoVencido(0);
      setSaldoVigente(0);
    } finally {
      setLoadingSaldo(false);
    }
  }, []);

  const cargarCliente = useCallback(
    async (nro: number) => {
      if (!nro || nro <= 0) return;
      try {
        const cliente = await clienteService.getCliente(nro, { soloOperativo: true });
        setFormulario((prev) => ({
          ...prev,
          nroCliente: nro,
          nombre: cliente.nombre || '',
          cuit: cliente.cuit || '',
          tipoIVA: cliente.tipoIva || '',
        }));
        await cargarSaldoCliente(nro);
      } catch (error: unknown) {
        if (esErrorClienteNoOperativo(error)) {
          enqueueSnackbar(getMensajeClienteNoOperativo(error), { variant: 'warning' });
        } else {
          enqueueSnackbar('No se pudo cargar el cliente', { variant: 'error' });
        }
      }
    },
    [cargarSaldoCliente, enqueueSnackbar],
  );

  useEffect(() => {
    const init = async () => {
      try {
        const [compList, sucRes] = await Promise.all([
          companiaService.getCompanias({ pageNumber: 1, pageSize: 100 }),
          api.get('/api/SucursalesVenta'),
        ]);

        const comps = (compList ?? [])
          .map((c) => ({
            idCompania: String(c.idCompania || '').trim(),
            nombre: String(c.nombre || '').trim(),
          }))
          .filter((c) => c.idCompania);
        setCompanias(comps);

        const sucData = sucRes.data;
        const sucItems: Sucursal[] = Array.isArray(sucData?.items)
          ? sucData.items
          : Array.isArray(sucData?.Items)
            ? sucData.Items
            : Array.isArray(sucData)
              ? sucData
              : [];
        setSucursales(sucItems);

        const primeraCompania = comps[0]?.idCompania ?? '';
        const nro = resolveNroSucParaCompania({
          idCompania: primeraCompania,
          sucursales: sucItems,
          keepCurrentIfValid: false,
        });
        setFormulario((prev) => ({
          ...prev,
          idCompania: primeraCompania || prev.idCompania,
          ...(nro != null && nro > 0 ? { nroSuc: nro } : {}),
        }));
        if (primeraCompania) persistGlobalIdCompania(primeraCompania);

        const qpCliente = Number(searchParams.get('nroCliente') || 0);
        if (qpCliente > 0) await cargarCliente(qpCliente);
      } catch {
        enqueueSnackbar('Error al inicializar el formulario', { variant: 'error' });
      } finally {
        setLoadingInit(false);
        setLoadingSucursales(false);
      }
    };
    init();
  }, [cargarCliente, enqueueSnackbar, searchParams]);

  const setNroSucFormulario = useCallback((nroSuc: number) => {
    setFormulario((prev) => (Number(prev.nroSuc) === nroSuc ? prev : { ...prev, nroSuc }));
  }, []);

  const { onSucursalChange } = useSucursalAltaFormulario({
    nroSuc: Number(formulario.nroSuc) || 0,
    setNroSuc: setNroSucFormulario,
    sucursales,
    loadingSucursales,
    enabled: true,
  });

  const sucursalesDeCompania = useMemo(
    () => filtrarSucursalesPorCompania(sucursales, formulario.idCompania),
    [sucursales, formulario.idCompania],
  );

  const handleFormularioChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent,
  ) => {
    const { name, value } = e.target;
    if (name === 'idCompania') {
      const id = String(value ?? '').trim();
      if (id) persistGlobalIdCompania(id);
      const nro = resolveNroSucParaCompania({
        idCompania: id,
        sucursales,
        currentNroSuc: formulario.nroSuc,
        keepCurrentIfValid: false,
      });
      setFormulario((prev) => ({
        ...prev,
        idCompania: id,
        nroSuc: nro != null && nro > 0 ? nro : 0,
      }));
      return;
    }
    setFormulario((prev) => ({
      ...prev,
      [name]: name === 'nroSuc' ? Number(value) || 0 : value,
    }));
  };

  const handleSucursalChange = (e: SelectChangeEvent<number | string>) => {
    const n = Number(e.target.value) || 0;
    onSucursalChange(n);
    const suc = sucursales.find((s) => s.nroSuc === n);
    const idFromSuc = String(suc?.idCompania ?? '').trim();
    if (idFromSuc) {
      setFormulario((prev) => ({ ...prev, idCompania: idFromSuc }));
      persistGlobalIdCompania(idFromSuc);
    }
  };

  const handleClienteSelect = (cliente: { nroCliente: number; nombre: string; cuit?: string; tipoIva?: string }) => {
    if (clienteBloqueado) {
      enqueueSnackbar('No se puede cambiar el cliente con un reintegro cargado', { variant: 'warning' });
      setOpenClienteSearch(false);
      return;
    }
    setFormulario((prev) => ({
      ...prev,
      nroCliente: cliente.nroCliente,
      nombre: cliente.nombre,
      cuit: cliente.cuit || '',
      tipoIVA: cliente.tipoIva || '',
    }));
    setOpenClienteSearch(false);
    void cargarSaldoCliente(cliente.nroCliente);
    enqueueSnackbar(`Cliente seleccionado: ${cliente.nombre}`, { variant: 'success' });
  };

  const ensureGrupoCajaSeleccionado = async (): Promise<boolean> => {
    try {
      setGrupoCajaLoading(true);
      const grupos = await tesoreriaService.getGruposCajaChica({ usuario: loggedUsername });
      setGruposCaja(grupos || []);

      const saved = leerGrupoCajaGuardadoRaw();
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const savedId = parsed?.id ?? parsed?.codigo ?? null;
          const found = grupos.find((g) => g.id === savedId || g.codigo === savedId);
          if (found) {
            setGrupoCajaSeleccionadoId(found.id ?? null);
            setGrupoCajaSeleccionado(found);
            setReintegroDraft((prev) => ({ ...prev, grupoMcp: found.codigo }));
            const medios = await tesoreriaService.getMediosEfectivoPorGrupo(found.codigo);
            setMediosEfectivo(medios);
            setEfectivoSeleccionadoId(null);
            setActiveStep(1);
            return true;
          }
        } catch {
          /* ignore */
        }
      }

      setOpenGrupoCajaDialog(true);
      return false;
    } catch {
      enqueueSnackbar('Error al cargar grupos de caja', { variant: 'error' });
      setOpenGrupoCajaDialog(true);
      return false;
    } finally {
      setGrupoCajaLoading(false);
    }
  };

  useEffect(() => {
    if (!openGrupoCajaDialog) return;
    const loadFirstPage = async () => {
      try {
        setGrupoCajaLoading(true);
        const PAGE_SIZE = 30;
        const data = await tesoreriaService.getGruposCajaChicaPaged({
          usuario: loggedUsername,
          pageNumber: 1,
          pageSize: PAGE_SIZE,
        });
        setGruposCaja(data || []);
        setGrupoCajaHasMore((data?.length || 0) > 0);
        setGrupoCajaPage(2);
      } catch {
        enqueueSnackbar('Error al cargar grupos de caja', { variant: 'error' });
        setGruposCaja([]);
        setGrupoCajaHasMore(false);
      } finally {
        setGrupoCajaLoading(false);
      }
    };
    loadFirstPage();
  }, [openGrupoCajaDialog, loggedUsername, enqueueSnackbar]);

  const handleGrupoCajaScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    if (!grupoCajaHasMore || grupoCajaLoading) return;
    const target = e.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 64) {
      const PAGE_SIZE = 30;
      void (async () => {
        try {
          setGrupoCajaLoading(true);
          const data = await tesoreriaService.getGruposCajaChicaPaged({
            usuario: loggedUsername,
            pageNumber: grupoCajaPage,
            pageSize: PAGE_SIZE,
          });
          const existingIds = new Set(gruposCaja.map((g) => g.id));
          const nuevos = (data || []).filter((g) => !existingIds.has(g.id));
          setGrupoCajaHasMore(nuevos.length > 0);
          if (nuevos.length > 0) {
            setGrupoCajaPage((prev) => prev + 1);
            setGruposCaja((prev) => [...prev, ...nuevos]);
          }
        } finally {
          setGrupoCajaLoading(false);
        }
      })();
    }
  };

  const handleConfirmarGrupoCaja = async () => {
    if (grupoCajaSeleccionadoId == null) return;
    const selected = gruposCaja.find((g) => g.id === grupoCajaSeleccionadoId);
    if (!selected) return;

    persistGrupoCajaChica(selected);
    setGrupoCajaSeleccionado(selected);
    setReintegroDraft((prev) => ({ ...prev, grupoMcp: selected.codigo }));
    setOpenGrupoCajaDialog(false);
    setActiveStep(1);

    try {
      const medios = await tesoreriaService.getMediosEfectivoPorGrupo(selected.codigo);
      setMediosEfectivo(medios);
      setEfectivoSeleccionadoId(null);
    } catch {
      enqueueSnackbar('No se pudieron cargar medios de efectivo', { variant: 'error' });
    }
  };

  const handleOpenReintegroDialog = async () => {
    if (!(formulario.nroCliente > 0) || !formulario.nombre) {
      enqueueSnackbar('Debe seleccionar un cliente antes de agregar el reintegro', { variant: 'warning' });
      return;
    }

    setReintegroDraft({
      ...initialReintegro,
      importe: reintegro?.importe ?? 0,
      detalle: reintegro?.detalle ?? '',
      mcp: reintegro?.mcp,
      medioLabel: reintegro?.medioLabel,
      grupoMcp: reintegro?.grupoMcp ?? grupoCajaSeleccionado?.codigo,
    });
    setActiveStep(0);
    setOpenReintegroDialog(true);
    setEfectivoSeleccionadoId(null);
    setMediosEfectivo([]);
    setMontoDraft('');

    const ok = await ensureGrupoCajaSeleccionado();
    if (ok) {
      setOpenReintegroDialog(true);
    }
  };

  const handleCloseReintegroDialog = () => {
    setOpenReintegroDialog(false);
    setReintegroDraft(initialReintegro);
    setActiveStep(0);
    setEfectivoSeleccionadoId(null);
    setMontoDraft('');
  };

  const handleReintegroFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setReintegroDraft((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleGuardarReintegro = () => {
    const importe = truncToDecimals(isMontoFocused ? montoDraft : reintegroDraft.importe);
    if (!Number.isFinite(importe) || importe <= 0) {
      enqueueSnackbar('Ingrese un importe válido mayor a cero', { variant: 'warning' });
      return;
    }
    if (!reintegroDraft.mcp) {
      enqueueSnackbar('Seleccione el medio de efectivo', { variant: 'warning' });
      return;
    }
    if (!grupoCajaSeleccionado?.codigo) {
      enqueueSnackbar('Seleccione un grupo de caja', { variant: 'warning' });
      return;
    }

    setReintegro({
      tipoPago: 'Efectivo',
      mcp: reintegroDraft.mcp,
      medioLabel: reintegroDraft.medioLabel,
      importe,
      detalle: reintegroDraft.detalle,
      grupoMcp: grupoCajaSeleccionado.codigo,
    });
    handleCloseReintegroDialog();
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();

    if (!(formulario.nroCliente > 0)) {
      enqueueSnackbar('Seleccione un cliente', { variant: 'warning' });
      return;
    }
    if (!(formulario.nroSuc > 0)) {
      enqueueSnackbar('Seleccione una sucursal', { variant: 'warning' });
      return;
    }
    if (!formulario.idCompania) {
      enqueueSnackbar('Seleccione la compañía emisora', { variant: 'warning' });
      return;
    }
    if (!reintegro?.mcp || !(reintegro.importe > 0)) {
      enqueueSnackbar('Agregue el reintegro en efectivo', { variant: 'warning' });
      return;
    }
    if (!reintegro.grupoMcp) {
      enqueueSnackbar('Falta el grupo de caja del reintegro', { variant: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const result = await devolucionVentaService.create({
        formulario: {
          idCompania: formulario.idCompania,
          nroCliente: formulario.nroCliente,
          nroSuc: formulario.nroSuc,
          importeTotal: reintegro.importe,
          descripcion: formulario.descripcion.trim() || `Devolución a ${formulario.nombre}`,
          fechaEmision: formulario.fechaEmision
            ? new Date(`${formulario.fechaEmision}T12:00:00`).toISOString()
            : new Date().toISOString(),
        },
        grupoMCP: reintegro.grupoMcp,
        medioReintegro: {
          mcp: reintegro.mcp,
          importe: reintegro.importe,
          detalle: reintegro.detalle || formulario.descripcion || undefined,
        },
      });

      const numero = formatComprobanteTesoreria(
        result.letra || 'X',
        result.nroSuc ?? formulario.nroSuc,
        result.nroComprobante
      );
      enqueueSnackbar(`Devolución ${numero} registrada`, { variant: 'success' });
      navigate('/tesoreria/devoluciones');
    } catch (error: unknown) {
      const data = (error as { response?: { data?: { mensaje?: string; message?: string } } })?.response?.data;
      const msg = data?.mensaje || data?.message || 'Error al registrar la devolución';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loadingInit) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            spacing={1.5}
            mb={2.5}
            flexWrap="wrap"
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/tesoreria/devoluciones')}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Devoluciones
            </Button>
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
            <Typography
              variant={isSmallScreen ? 'h5' : 'h4'}
              sx={{ fontWeight: 600, color: '#1976d2', fontSize: { xs: '1.5rem', sm: '2rem' } }}
            >
              Nueva devolución
            </Typography>
          </Stack>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="flex-start">
              <Grid item xs={12} md={8}>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  display="block"
                  mb={0.75}
                  sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                >
                  Cliente
                </Typography>
                {!(formulario.nroCliente > 0 && formulario.nombre) ? (
                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    fullWidth
                    disabled={clienteBloqueado}
                    onClick={() => setOpenClienteSearch(true)}
                    sx={{
                      py: 3,
                      px: 2,
                      borderStyle: 'dashed',
                      borderRadius: 2,
                      textTransform: 'none',
                      flexDirection: 'column',
                      gap: 1,
                      color: 'primary.main',
                    }}
                  >
                    <PersonSearch sx={{ fontSize: 40 }} />
                    Seleccionar cliente
                  </Button>
                ) : (
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                        <Box flex={1} minWidth={0}>
                          <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap" useFlexGap>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {formulario.nombre}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              #{formulario.nroCliente}
                            </Typography>
                          </Stack>
                          {formulario.cuit ? (
                            <Typography variant="body2" color="text.secondary">
                              CUIT: {formulario.cuit}
                            </Typography>
                          ) : null}
                          {formulario.tipoIVA ? (
                            <Box mt={0.75}>
                              <Chip
                                label={TIPO_IVA_LABELS[formulario.tipoIVA] ?? formulario.tipoIVA}
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: '0.7rem' }}
                              />
                            </Box>
                          ) : null}
                        </Box>
                        {!clienteBloqueado && (
                          <Tooltip title="Cambiar cliente">
                            <IconButton
                              size="small"
                              onClick={() => setOpenClienteSearch(true)}
                              aria-label="Cambiar cliente"
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                )}
                {clienteBloqueado && (
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                    No se puede modificar el cliente cuando hay un reintegro cargado
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
                  <Box
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      textTransform="uppercase"
                      letterSpacing={0.5}
                      display="block"
                      mb={1.5}
                    >
                      Datos de emisión
                    </Typography>
                    <Stack spacing={1.5}>
                      {loadingSucursales ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                          <CircularProgress size={22} />
                        </Box>
                      ) : (
                        <FormControl size="small" fullWidth required>
                          <InputLabel>Sucursal</InputLabel>
                          <Select
                            value={formulario.nroSuc > 0 ? formulario.nroSuc : ''}
                            onChange={handleSucursalChange}
                            label="Sucursal"
                          >
                            {sucursalesDeCompania.map((sucursal) => (
                              <MenuItem key={sucursal.nroSuc} value={sucursal.nroSuc}>
                                {sucursal.nombreSucursal?.trim()
                                  ? `${sucursal.nroSuc} — ${sucursal.nombreSucursal.trim()}`
                                  : `Suc. ${sucursal.nroSuc}`}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                      <FormControl size="small" fullWidth required>
                        <InputLabel>Compañía</InputLabel>
                        <Select
                          name="idCompania"
                          value={formulario.idCompania}
                          label="Compañía"
                          onChange={handleFormularioChange}
                        >
                          {companias.map((c) => (
                            <MenuItem key={c.idCompania} value={c.idCompania}>
                              {c.idCompania} — {c.nombre}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        fullWidth
                        label="Fecha de emisión"
                        name="fechaEmision"
                        type="date"
                        value={formulario.fechaEmision}
                        onChange={handleFormularioChange}
                        InputLabelProps={{ shrink: true }}
                        required
                      />
                      <TextField
                        size="small"
                        fullWidth
                        label="Observaciones"
                        name="descripcion"
                        value={formulario.descripcion}
                        onChange={handleFormularioChange}
                        multiline
                        minRows={2}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                              <NotesIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 1 }} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Card sx={{ boxShadow: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      display="block"
                      mb={1.5}
                      sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                    >
                      Cuenta corriente
                    </Typography>
                    {loadingSaldo ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} />
                      </Box>
                    ) : formulario.nroCliente <= 0 ? (
                      <Box sx={{ py: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Seleccione un cliente para ver su saldo
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                              Vigente:
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 700, color: saldoVigente < 0 ? 'success.main' : 'text.primary' }}
                            >
                              {formatCurrency(saldoVigente)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                              Vencido:
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 700, color: saldoVencido < 0 ? 'success.main' : 'error.main' }}
                            >
                              {formatCurrency(saldoVencido)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                              Saldo total:
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 700,
                                color: 'white',
                                bgcolor: saldoTotal < 0 ? 'success.main' : 'error.main',
                                borderRadius: 1.5,
                                px: 1.5,
                                py: 0.75,
                                display: 'inline-block',
                                minWidth: '170px',
                                textAlign: 'center',
                              }}
                            >
                              {formatCurrency(saldoTotal)}
                            </Typography>
                          </Grid>
                        </Grid>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          La devolución restará saldo en cuenta corriente y egresará efectivo de caja en una sola operación.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card sx={{ boxShadow: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      display="block"
                      mb={1.5}
                      sx={{ letterSpacing: 0.5, textTransform: 'uppercase' }}
                    >
                      Medio de reintegro
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        mb: 2,
                        gap: 1,
                        flexDirection: { xs: 'column', sm: 'row' },
                      }}
                    >
                      <Button
                        type="button"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => void handleOpenReintegroDialog()}
                        disabled={!(formulario.nroCliente > 0) || !formulario.nombre}
                        fullWidth={isSmallScreen}
                      >
                        {reintegro ? 'Editar reintegro' : 'Agregar reintegro'}
                      </Button>
                    </Box>

                    {reintegro ? (
                      <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 0 }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                              <Typography sx={{ fontWeight: 800, color: 'primary.main' }}>EFECTIVO</Typography>
                              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                {reintegro.medioLabel || reintegro.mcp || 'CAJA CHICA'}
                              </Typography>
                              {reintegro.detalle ? (
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block', mt: 0.5 }}
                                >
                                  {reintegro.detalle}
                                </Typography>
                              ) : null}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontWeight: 800, color: 'error.main' }}>
                                {formatCurrency(reintegro.importe)}
                              </Typography>
                              <IconButton
                                onClick={() => setReintegro(null)}
                                size="small"
                                sx={{ color: 'text.secondary' }}
                                aria-label="Quitar reintegro"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Aún no has agregado el reintegro en efectivo.
                      </Typography>
                    )}

                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: 2,
                        borderLeft: '6px solid',
                        borderColor: 'primary.main',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography color="text.secondary">Importe a devolver:</Typography>
                        <Typography sx={{ color: 'error.main', fontWeight: 800 }}>
                          {formatCurrency(totalReintegro)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="warning"
                        fullWidth
                        disabled={saving || totalReintegro <= 0}
                      >
                        {saving ? 'Registrando...' : 'Registrar devolución'}
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </form>

          <Dialog
            open={openReintegroDialog}
            onClose={handleCloseReintegroDialog}
            maxWidth="sm"
            fullWidth
            fullScreen={isSmallScreen}
          >
            <DialogTitle>Agregar reintegro</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                  <Step>
                    <StepLabel>Grupo de caja</StepLabel>
                  </Step>
                  <Step>
                    <StepLabel>Efectivo</StepLabel>
                  </Step>
                </Stepper>

                {activeStep === 1 ? (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        EFECTIVO — Seleccione el medio específico:
                      </Typography>
                      <Box sx={{ maxHeight: { xs: '50vh', sm: 240 }, overflowY: 'auto', pr: 1 }}>
                        <Grid container spacing={2}>
                          {mediosEfectivo.map((medio) => (
                            <Grid item xs={12} key={medio.id}>
                              <Card
                                variant={efectivoSeleccionadoId === medio.id ? 'elevation' : 'outlined'}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => {
                                  setEfectivoSeleccionadoId(medio.id);
                                  setReintegroDraft((prev) => ({
                                    ...prev,
                                    medioLabel: medio.nombre || medio.descripcion,
                                    mcp: medio.mcp || medio.nombre,
                                  }));
                                }}
                              >
                                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <input
                                    type="radio"
                                    checked={efectivoSeleccionadoId === medio.id}
                                    onChange={() => {
                                      setEfectivoSeleccionadoId(medio.id);
                                      setReintegroDraft((prev) => ({
                                        ...prev,
                                        medioLabel: medio.nombre || medio.descripcion,
                                        mcp: medio.mcp || medio.nombre,
                                      }));
                                    }}
                                    style={{ marginRight: 8 }}
                                  />
                                  <Box>
                                    <Typography sx={{ fontWeight: 700 }}>{medio.mcp || medio.nombre}</Typography>
                                    {medio.descripcion ? (
                                      <Typography variant="caption" display="block">
                                        {medio.descripcion}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                          {mediosEfectivo.length === 0 && (
                            <Grid item xs={12}>
                              <Typography variant="body2">
                                No hay medios de efectivo disponibles para este grupo.
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Monto a devolver"
                        name="importe"
                        type="number"
                        value={
                          isMontoFocused
                            ? montoDraft
                            : Number.isFinite(reintegroDraft.importe)
                              ? truncToDecimals(reintegroDraft.importe).toFixed(2)
                              : ''
                        }
                        onChange={(e) => setMontoDraft(e.target.value)}
                        onFocus={() => {
                          setIsMontoFocused(true);
                          setMontoDraft(
                            reintegroDraft.importe === 0 ? '' : String(reintegroDraft.importe)
                          );
                        }}
                        onBlur={() => {
                          const importe = truncToDecimals(montoDraft);
                          setIsMontoFocused(false);
                          setMontoDraft('');
                          setReintegroDraft((prev) => ({ ...prev, importe }));
                        }}
                        inputProps={{ step: '0.01', min: 0.01, inputMode: 'decimal' }}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Observaciones (opcional)"
                        name="detalle"
                        value={reintegroDraft.detalle ?? ''}
                        onChange={handleReintegroFieldChange}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Seleccione el grupo de caja para continuar.
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseReintegroDialog}>Cancelar</Button>
              {activeStep === 1 ? (
                <Button
                  onClick={handleGuardarReintegro}
                  variant="contained"
                  disabled={!reintegroDraft.mcp || !(Number(reintegroDraft.importe) > 0)}
                >
                  Guardar
                </Button>
              ) : null}
            </DialogActions>
          </Dialog>

          <Dialog
            open={openGrupoCajaDialog}
            onClose={() => setOpenGrupoCajaDialog(false)}
            maxWidth="sm"
            fullWidth
            fullScreen={isSmallScreen}
          >
            <DialogTitle>Seleccionar grupo de caja</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 1 }}>
                <Box
                  sx={{ maxHeight: { xs: '60vh', sm: 360 }, overflowY: 'auto', pr: 1 }}
                  onScroll={handleGrupoCajaScroll}
                >
                  <Grid container spacing={2}>
                    {gruposCaja.map((g) => (
                      <Grid item xs={12} key={g.id}>
                        <Card
                          variant={grupoCajaSeleccionadoId === g.id ? 'elevation' : 'outlined'}
                          sx={{ cursor: 'pointer' }}
                          onClick={() => setGrupoCajaSeleccionadoId(g.id)}
                        >
                          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <input
                              type="radio"
                              checked={grupoCajaSeleccionadoId === g.id}
                              onChange={() => setGrupoCajaSeleccionadoId(g.id)}
                              style={{ marginRight: 8 }}
                            />
                            <Box>
                              <Typography sx={{ fontWeight: 600 }}>{g.nombre}</Typography>
                              <Typography variant="caption">Código: {g.codigo}</Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                    {gruposCaja.length === 0 && !grupoCajaLoading && (
                      <Grid item xs={12}>
                        <Typography variant="body2">No hay grupos de caja disponibles.</Typography>
                      </Grid>
                    )}
                    {grupoCajaLoading && (
                      <Grid item xs={12}>
                        <Typography variant="body2">Cargando grupos...</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenGrupoCajaDialog(false)}>Cancelar</Button>
              <Button
                onClick={() => void handleConfirmarGrupoCaja()}
                variant="contained"
                disabled={grupoCajaSeleccionadoId == null}
              >
                Seleccionar
              </Button>
            </DialogActions>
          </Dialog>

          <ClienteSearch
            open={openClienteSearch}
            onClose={() => setOpenClienteSearch(false)}
            onSelect={handleClienteSelect}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/tesoreria/devoluciones')}
              disabled={saving}
              sx={{ borderRadius: 2, px: 4, py: 1.5, textTransform: 'none', fontWeight: 500 }}
            >
              Cancelar
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default NuevaDevolucion;
